import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  NativeSelect,
  Spinner,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  RefreshCwIcon,
  ShieldAlertIcon,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { PageHeader } from '~/components/shell'
import { useAuth } from '~/contexts/AuthContext'
import { activityApi, type PageActivityCount } from '~/lib/api'

export const Route = createFileRoute('/_app/admin/activity-pages')({
  component: ActivityPagesPage,
})

const PAGE_SIZE = 20

function formatPath(path: string) {
  // Make paths more readable
  if (path === '/') return 'Home'
  return path
    .replace(/^\//, '')
    .replace(/\//g, ' > ')
    .replace(/-/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function ActivityPagesPage() {
  const { hasMinRole, isLoading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [currentPage, setCurrentPage] = useState(1)
  const [statsDays, setStatsDays] = useState(7)

  const isAdmin = hasMinRole('admin')

  // Redirect non-admins after a short delay
  useEffect(() => {
    if (!authLoading && !isAdmin) {
      const timer = setTimeout(() => {
        navigate({ to: '/' })
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [authLoading, isAdmin, navigate])

  // Show access denied for non-admins
  if (!authLoading && !isAdmin) {
    return (
      <Flex h="100%" align="center" justify="center">
        <VStack gap={4} textAlign="center" p={8}>
          <Box p={4} borderRadius="full" bg="red.100" _dark={{ bg: 'red.900' }}>
            <ShieldAlertIcon size={48} color="var(--chakra-colors-red-500)" />
          </Box>
          <Heading size="lg" color="red.500">
            Access Denied
          </Heading>
          <Text color="content.secondary" maxW="400px">
            This page is restricted to <strong>admin</strong> users only.
          </Text>
          <Text fontSize="sm" color="content.secondary">
            Redirecting to home page...
          </Text>
        </VStack>
      </Flex>
    )
  }

  // Fetch pages data
  const {
    data: pagesData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['activity-pages', statsDays, currentPage],
    queryFn: () => activityApi.pages({ days: statsDays, page: currentPage, pageSize: PAGE_SIZE }),
    retry: false,
  })

  const pages = pagesData?.pages || []
  const totalPages = pagesData?.total || 0
  const totalPaginationPages = Math.ceil(totalPages / PAGE_SIZE)

  return (
    <Box maxW="1400px" mx="auto">
      <Button variant="ghost" size="sm" mb={2} onClick={() => navigate({ to: '/admin/activity' })}>
        <ArrowLeftIcon size={16} />
        Back to Activity
      </Button>
      <PageHeader
        title="Page Activity"
        subtitle="All pages ordered by views"
        actions={
          <>
            <NativeSelect.Root size="sm" w="160px">
              <NativeSelect.Field
                value={statsDays}
                onChange={(e) => {
                  setStatsDays(Number(e.target.value))
                  setCurrentPage(1)
                }}
              >
                <option value={1}>Last 24 hours</option>
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </NativeSelect.Field>
            </NativeSelect.Root>
            <Button size="sm" onClick={() => refetch()}>
              <RefreshCwIcon size={16} />
              Refresh
            </Button>
          </>
        }
      />

      <VStack align="stretch" gap={6}>
        {/* Pages Table */}
        <Card.Root>
          <Card.Header>
            <Flex justify="space-between" align="center">
              <Heading size="sm">Pages by Views</Heading>
              <Text fontSize="sm" color="content.secondary">
                {totalPages} total pages
              </Text>
            </Flex>
          </Card.Header>
          <Table.Root>
            <Table.Header>
              <Table.Row bg="bg.subtle">
                <Table.ColumnHeader w="60px">Rank</Table.ColumnHeader>
                <Table.ColumnHeader>Page</Table.ColumnHeader>
                <Table.ColumnHeader w="150px" textAlign="right">
                  Views
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {isLoading ? (
                <Table.Row>
                  <Table.Cell colSpan={3}>
                    <Flex justify="center" py={8}>
                      <Spinner />
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ) : pages.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={3}>
                    <Text textAlign="center" py={8} color="content.secondary">
                      No page activity found
                    </Text>
                  </Table.Cell>
                </Table.Row>
              ) : (
                pages.map((page: PageActivityCount, index: number) => {
                  const rank = (currentPage - 1) * PAGE_SIZE + index + 1
                  return (
                    <Table.Row key={page.pagePath}>
                      <Table.Cell>
                        <Badge
                          colorPalette={
                            rank === 1
                              ? 'yellow'
                              : rank === 2
                                ? 'gray'
                                : rank === 3
                                  ? 'orange'
                                  : 'blue'
                          }
                          variant="subtle"
                          px={2}
                        >
                          #{rank}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <VStack align="flex-start" gap={0}>
                          <Text fontSize="sm" fontWeight="medium">
                            {formatPath(page.pagePath)}
                          </Text>
                          <Text fontSize="xs" color="content.secondary">
                            {page.pagePath}
                          </Text>
                        </VStack>
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        <Badge colorPalette="green" variant="subtle" fontSize="sm">
                          {page.pageViews} views
                        </Badge>
                      </Table.Cell>
                    </Table.Row>
                  )
                })
              )}
            </Table.Body>
          </Table.Root>
        </Card.Root>

        {/* Pagination */}
        {totalPaginationPages > 1 && (
          <Flex justify="space-between" align="center">
            <Text fontSize="sm" color="content.secondary">
              Showing {(currentPage - 1) * PAGE_SIZE + 1}-
              {Math.min(currentPage * PAGE_SIZE, totalPages)} of {totalPages} pages
            </Text>
            <HStack gap={2}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeftIcon size={16} />
                Previous
              </Button>
              <HStack gap={1}>
                {Array.from({ length: Math.min(totalPaginationPages, 5) }, (_, i) => {
                  let pageNum: number
                  if (totalPaginationPages <= 5) {
                    pageNum = i + 1
                  } else if (currentPage <= 3) {
                    pageNum = i + 1
                  } else if (currentPage >= totalPaginationPages - 2) {
                    pageNum = totalPaginationPages - 4 + i
                  } else {
                    pageNum = currentPage - 2 + i
                  }
                  return (
                    <Button
                      key={pageNum}
                      size="sm"
                      variant={pageNum === currentPage ? 'solid' : 'outline'}
                      colorPalette={pageNum === currentPage ? 'blue' : undefined}
                      onClick={() => setCurrentPage(pageNum)}
                      minW="40px"
                    >
                      {pageNum}
                    </Button>
                  )
                })}
              </HStack>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentPage((p) => Math.min(totalPaginationPages, p + 1))}
                disabled={currentPage === totalPaginationPages}
              >
                Next
                <ChevronRightIcon size={16} />
              </Button>
            </HStack>
          </Flex>
        )}
      </VStack>
    </Box>
  )
}
